import React from "react";
import {
  Paper,
  Textarea,
  TextInput,
  Group,
  Button,
  ActionIcon,
  Radio,
  Stack,
  Text,
} from "@mantine/core";
import { IconTrash, IconPlus } from "@tabler/icons";
import FormReadOnlyField from "../formReadOnlyField";

const QuizQuestionBuilder = ({ questions = [], onChange, readOnly }) => {
  const updateQuestion = (index, patch) => {
    const next = questions.map((q, i) => (i === index ? { ...q, ...patch } : q));
    onChange?.(next);
  };

  const updateOption = (qIndex, oIndex, patch) => {
    const next = questions.map((q, i) => {
      if (i !== qIndex) return q;
      const options = (q.options ?? []).map((o, j) =>
        j === oIndex ? { ...o, ...patch } : o
      );
      return { ...q, options };
    });
    onChange?.(next);
  };

  const setCorrect = (qIndex, oIndex) => {
    const next = questions.map((q, i) => {
      if (i !== qIndex) return q;
      return {
        ...q,
        options: (q.options ?? []).map((o, j) => ({
          ...o,
          isCorrect: j === oIndex,
        })),
      };
    });
    onChange?.(next);
  };

  const addQuestion = () => {
    onChange?.([
      ...questions,
      {
        questionText: "",
        options: [
          { text: "", isCorrect: true },
          { text: "", isCorrect: false },
          { text: "", isCorrect: false },
          { text: "", isCorrect: false },
        ],
        order: questions.length,
      },
    ]);
  };

  const removeQuestion = (index) => {
    onChange?.(questions.filter((_, i) => i !== index));
  };

  const addOption = (qIndex) => {
    const q = questions[qIndex];
    if ((q.options?.length ?? 0) >= 6) return;
    updateQuestion(qIndex, {
      options: [...(q.options ?? []), { text: "", isCorrect: false }],
    });
  };

  const removeOption = (qIndex, oIndex) => {
    const q = questions[qIndex];
    if ((q.options?.length ?? 0) <= 2) return;
    updateQuestion(qIndex, {
      options: q.options.filter((_, j) => j !== oIndex),
    });
  };

  return (
    <Stack spacing="md">
      {questions.map((question, qIndex) => (
        <Paper key={question._id ?? qIndex} p="md" withBorder radius="md">
          <Group position="apart" mb="sm">
            <Text weight={600}>Question {qIndex + 1}</Text>
            {!readOnly && (
              <ActionIcon onClick={() => removeQuestion(qIndex)}>
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>

          {readOnly ? (
            <FormReadOnlyField
              label="Question"
              value={question.questionText || "-"}
              preLine
            />
          ) : (
            <Textarea
              label="Question"
              minRows={2}
              value={question.questionText ?? ""}
              onChange={(e) =>
                updateQuestion(qIndex, { questionText: e.currentTarget.value })
              }
            />
          )}

          <Stack spacing="xs" mt="md">
            {(question.options ?? []).map((option, oIndex) => (
              <Group key={oIndex} spacing="sm" noWrap align="flex-start">
                {readOnly ? (
                  <Text size="sm" weight={option.isCorrect ? 700 : 500}>
                    {option.text || "-"}
                    {option.isCorrect ? " (Correct)" : ""}
                  </Text>
                ) : (
                  <>
                <Radio
                  checked={option.isCorrect}
                  onChange={() => setCorrect(qIndex, oIndex)}
                  mt={8}
                />
                <TextInput
                  sx={{ flex: 1 }}
                  placeholder={`Option ${oIndex + 1}`}
                  value={option.text ?? ""}
                  onChange={(e) =>
                    updateOption(qIndex, oIndex, { text: e.currentTarget.value })
                  }
                />
                {!readOnly && (question.options?.length ?? 0) > 2 && (
                  <ActionIcon onClick={() => removeOption(qIndex, oIndex)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                )}
                  </>
                )}
              </Group>
            ))}
          </Stack>

          {!readOnly && (question.options?.length ?? 0) < 6 && (
            <Button
              variant="subtle"
              size="xs"
              leftIcon={<IconPlus size={14} />}
              mt="sm"
              onClick={() => addOption(qIndex)}
            >
              Add option
            </Button>
          )}
        </Paper>
      ))}

      {!readOnly && (
        <Button
          size="xs"
          leftIcon={<IconPlus size={16} />}
          onClick={addQuestion}
          sx={{ alignSelf: "flex-start" }}
        >
          Add Question
        </Button>
      )}
    </Stack>
  );
};

export default QuizQuestionBuilder;
